/**
 * 智能体中心相关 API
 */
import { get, post, del, put } from '@/utils/request'
import {
  getDeApiErrorMessage,
  getUserApiKey,
  isDeApiSuccess,
} from '@/utils/digitalEmployeeProject'

function extractMultimodalTextContent(data) {
  if (!data) return ''
  if (typeof data === 'string') return data.trim()
  if (typeof data === 'object') {
    if (data.content != null) return extractMultimodalTextContent(data.content)
    if (data.data != null) return extractMultimodalTextContent(data.data)
    if (data.text != null) return String(data.text)
    if (data.result != null) return extractMultimodalTextContent(data.result)
    if (data.output != null) return extractMultimodalTextContent(data.output)
    if (data.choices && data.choices[0]) {
      const choice = data.choices[0]
      return extractMultimodalTextContent(choice.message?.content || choice.text)
    }
  }
  return ''
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** 后端 SSE 不可用时，将完整文本按字渐进回调，保持帮写弹窗流式体验 */
async function emitSimulatedStreamChunks(text, onChunk, signal) {
  const full = String(text || '')
  if (!onChunk || !full) return
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reducedMotion) {
    onChunk(full, full)
    return
  }
  let accumulated = ''
  const chars = Array.from(full)
  for (let i = 0; i < chars.length; i += 1) {
    if (signal?.aborted) {
      if (accumulated) onChunk('', accumulated)
      return
    }
    accumulated += chars[i]
    onChunk(chars[i], accumulated)
    await sleep(18)
  }
}

async function invokeNonStreamTextWithSimulatedStream(data, { onChunk, signal } = {}) {
  const res = await getMultimodalTextGenerationModel(data)
  if (!isDeApiSuccess(res)) {
    throw new Error(getDeApiErrorMessage(res, 'AI生成失败'))
  }
  const text = extractMultimodalTextContent(res.data != null ? res.data : res).trim()
  if (!text) throw new Error('AI生成未返回有效内容')
  await emitSimulatedStreamChunks(text, onChunk, signal)
  return text
}

/** 数字员工接口：Header 携带 user-key；走站点根路径 /ai_employee（非 /agent-dev） */
function withUserKey(options = {}) {
  const userKey = getUserApiKey()
  const next = {
    ...options,
    apiScope: 'siteRoot',
  }
  if (!userKey) return next
  return {
    ...next,
    headers: {
      ...(options.headers || {}),
      'user-key': userKey,
    },
  }
}

/** 获取智能体列表 */
export async function getAgentList(params = {}) {
  return get('/agentHub/list', params)
}

/** 获取智能体导航分类导航 */
export async function getAgentCategory(params = {}) {
  return get('/agentHub/categories', params)
}

/** 获取智能体标签 */
export async function getAgentTag(params = {}) {
  return get('/agentHub/tags', params)
}



/** 进入智能体详情页记录一次使用（Header 携带 token，body: { agentId }） */
export async function useAgentRecords(data) {
  return post('/agentHub/use', data)
}

/** 收藏/取消收藏智能体（Header 携带 token，body: { agentId }） */
export async function collectAgent(data) {
  return post('/agentHub/favorite/toggle', data)
}

/** 智能体收藏列表（Header 携带 token，query: pageNum、pageSize） */
export async function favoriteAgentsList(params = {}) {
  return get('/agentHub/favorite/list', params)
}

/** 智能体详情（query: agentId） */
export async function getAgentDetail(params = {}) {
  return get('/agentHub/detail', params)
}

/** 工作流商店接口（与 `workflow.js` 一致，便于从 agents 模块统一按需引用） */
export { getWorkflowList, getWorkTypeList, getWorkflowDetail } from './workflow'

/**
 * 数字员工项目列表，此接口需要header携带user-key
 */
export async function getDigitalEmployeeProjectList(params = {}) {
  return get('/ai_employee/projects', params, withUserKey())
}
/**
 * 添加数字员工项目，此接口需要header携带user-key
 */
export async function addDigitalEmployeeProject(data) {
  return post('/ai_employee/projects', data, withUserKey())
}
/**
 * 删除数字员工项目，此接口需要header携带user-key，DEL方法
 * @param {string|number} projectId
 */
export async function deleteDigitalEmployeeProject(projectId) {
  return del(`/ai_employee/projects/${projectId}`, withUserKey())
}
/**
 * 数字员工项目修改，此接口需要header携带user-key，PUT方法
 * @param {string|number} projectId
 */
export async function updateDigitalEmployeeProject(projectId, data = {}) {
  return put(`/ai_employee/projects/${projectId}`, data, withUserKey())
}
/**
 * 数字员工项目详情，此接口需要header携带user-key，GET方法
 */
export async function getDigitalEmployeeProjectDetail(projectId, params = {}) {
  return get(`/ai_employee/agent-projects-step/${projectId}`, params, withUserKey())
}
/**
 * 数字员工项目详情添加，此接口需要header携带user-key，POST方法
 */
export async function addDigitalEmployeeProjectDetail(data, step, projectId) {
  return post(`/ai_employee/agent-projects-step/${projectId}/steps/${step}/save`, data, withUserKey())
}

/**
 * 数字员工项目详情修改，此接口需要header携带user-key，POST方法
 * @param {object} data - 须含 id（当前节点 id）；单步修改 pid=0、sort=0；分步修改 pid=父节点 id、sort=子节点序号
 */
export async function updateDigitalEmployeeProjectDetail(data, step, projectId) {
  return post(`/ai_employee/agent-projects-step/${projectId}/steps/${step}/edit`, data, withUserKey())
}

/**
 * 多模态文字生成模型接口-普通
 */
export async function getMultimodalTextGenerationModel(data) {
  return post('/ai_employee/ai-texts/generate', data, withUserKey())
}
/**
 * 多模态文字生成模型接口-流式（入参与 getMultimodalTextGenerationModel 一致）
 * @param {object} data
 * @param {{ onChunk?: (delta: string, accumulated: string) => void, signal?: AbortSignal }} [streamOptions]
 */
export async function getMultimodalTextGenerationModelStream(data, streamOptions = {}) {
  const { onChunk, signal } = streamOptions
  // 线上 generate(SSE) / stream 端点暂返回 500，仅调一次普通 generate，前端模拟流式展示
  return invokeNonStreamTextWithSimulatedStream(data, { onChunk, signal })
}
/**
 * 图片生成模型
 */
export async function getImageGenerationModel(data) {
  return post('/ai_employee/ai-images/generate', data, withUserKey())
}
/**
 * 图片生成任务查询，用户轮询查询生成任务状态
 */
export async function getImageGenerationTask(taskId) {
  return get(`/ai_employee/ai-images/tasks/${taskId}`, {}, withUserKey())
}
/**
 * 视频生成模型
 */
export async function getVideoGenerationModel(data) {
  return post('/ai_employee/ai-videos/generate', data, withUserKey())
}
/**
 * 视频生成任务查询，用户轮询查询生成任务状态
 */
export async function getVideoGenerationTask(taskId) {
  return get(`/ai_employee/ai-videos/tasks/${taskId}`, {}, withUserKey())
}
/**
 * 获取提示词模板（query: templateKey，如 ds_key0）
 * @param {string} templateKey - 模板键
 */
export async function getPrompt(templateKey) {
  return get('/ai_employee/prompts/templatesDetail', { templateKey }, withUserKey())
}
/**
 * 智能体任务详情步骤删除（steps 后为步骤序号：0、1、2…，非节点数据库 id）
 * @param {string|number} taskId 项目 ID
 * @param {string|number} stepOrder 步骤序号
 */
export async function deleteAgentTaskDetail(taskId, stepOrder) {
  return del(`/ai_employee/agent-projects-step/${taskId}/steps/${stepOrder}`, withUserKey())
}

/**
 * 数字人资产列表
 */
export async function getDigitalEmployeeAssetList(params = {}) {
  return get('/sample/list', params)
}

/** 首页大 Banner 固定分类名（英文标识，避免中文 query 乱码） */
export const HOME_BANNER_TYPE_NAME = 'banner'

/**
 * 内容分类
 */
export async function getContentCategory(params = {}) {
  return get('/content/types', params)
}
/**
 * 内容列表
 * @param {Object} params
 * @param {number|string} [params.sortType=0] 排序：0 最新，1 最受欢迎，2 最多人浏览（必传，默认 0）
 */
export async function getContentList(params = {}) {
  const { typeName, sortType, ...rest } = params
  const resolvedSortType = Number(sortType)
  return get('/content/list', {
    ...rest,
    sortType: Number.isFinite(resolvedSortType) ? resolvedSortType : 0,
    ...(typeName != null && typeName !== '' ? { typeName: String(typeName) } : {}),
  })
}
/**
 * 首页大 Banner 内容列表（typeName 固定为 banner）
 */
export async function getHomeBannerContentList(params = {}) {
  return getContentList({
    pageNum: 1,
    pageSize: 10,
    typeName: String(HOME_BANNER_TYPE_NAME),
    ...params,
  })
}
/**
 * 内容详情
 */
export async function getContentDetail(params = {}) {
  return get('/content/detail', params)
}
